/* eslint-disable */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from '../styles/analytics.module.css';

// Slide-in drawer from right. Backdrop + escape key + body scroll lock.
// Content via render prop `children` or `render`.
//
// Renders into document.body via createPortal (Phase 2.6 hotfix) to escape
// parent stacking contexts. Essential when a consumer mounts the drawer
// inside a fixed-positioned ancestor that has its own z-index (e.g.
// AppHeader's root at position:fixed + zIndex:var(--z-elevated)). Without
// portaling, the drawer's z-index resolution is locked to the local
// stacking context and cannot clear siblings of the ancestor — no z-index
// value (1000, 9999, etc.) can escape a stacking context trap. The 5
// pre-T-015 consumers (DesignersMiniBoard, CrossMatrix, FactoryDeepDive,
// TrendsDrawer, ArchiveDrawer) continue to work identically because their
// parents (Analytics views, Archive view) do not create stacking-context
// traps; visual outcome unchanged for them.
//
// Props:
//   open, onClose      — visibility control
//   title, children, render — content
//   layer              — optional 'modal' to elevate above portal UI.
//                        Default (undefined) preserves existing
//                        --z-dropdown behavior for the 5 pre-T-015 consumers.
//                        T-015 ActivityDrawer passes 'modal' (Phase 2.5 hotfix).

export function Drawer({ open, onClose, title, children, render, layer }) {
  // Body scroll lock + escape key handling
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e) { if (e.key === 'Escape') onClose && onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const content = render ? render() : children;
  const zStyle = layer === 'modal' ? { zIndex: 'var(--z-modal)' } : undefined;

  return createPortal(
    <>
      <div className={styles.drawerBackdrop} style={zStyle} onClick={onClose} aria-hidden="true" />
      <aside
        className={styles.drawer}
        style={zStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Drawer'}
      >
        <div className={styles.drawerHead}>
          {title && (
            <h2 className={styles.sectionHeadTitle} style={{ fontSize: 22 }}>
              {title}
            </h2>
          )}
          <button type="button" className={styles.drawerClose} onClick={onClose}>
            Close
          </button>
        </div>
        <div>{content}</div>
      </aside>
    </>,
    document.body
  );
}
